import { clsx, type ClassValue } from 'clsx';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  formatDistanceToNow,
} from 'date-fns';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a date to a string in the format "MM/DD/YYYY HH:MM"
 * @param date - The date to format
 * @param maxDaysToShowDateAsDistanceToNow - The maximum number of days to show the date as distance to now
 * @returns The formatted date string
 */
export const formatDateDistanceToNowDynamic = (
  date: Date | string,
  maxDaysToShowDateAsDistanceToNow: number = 7,
) => {
  const MAX_TIME_TO_SHOW_DATE = 1000 * 60 * 60 * 24;

  const joinDate = new Date(date);
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - joinDate.getTime()) / MAX_TIME_TO_SHOW_DATE,
  );

  return diffInDays <= maxDaysToShowDateAsDistanceToNow
    ? formatDistanceToNow(joinDate, {
        addSuffix: true,
      })
    : format(joinDate, 'MMMM dd, yyyy');
};

/**
 * Formats a timestamp to a string in the format "X seconds/minutes/hours/days/months ago"
 * @param timestamp - The timestamp to format
 * @returns The formatted timestamp string
 */
export const formatTimestamp = (timestamp: string): string => {
  const now = new Date();
  const seconds = differenceInSeconds(now, timestamp);
  const minutes = differenceInMinutes(now, timestamp);
  const hours = differenceInHours(now, timestamp);
  const days = differenceInDays(now, timestamp);

  if (seconds < 60) {
    return `${seconds} seconds ago`;
  } else if (minutes < 60) {
    return `${minutes} minutes ago`;
  } else if (hours < 24) {
    return `${hours} hours ago`;
  } else if (days < 30) {
    return `${days} days ago`;
  } else {
    return format(timestamp, "MM 'months ago,' yyyy");
  }
};
